<template>
  <div v-if="loading">
    <v-toolbar class="title video-modal-title"></v-toolbar>
    <v-card class="d-flex justify-center align-center" height="200">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
        >
      </v-progress-circular>
    </v-card>
  </div>
  <div v-else-if="error">
    <div style="height: 430px">
      <v-alert
        variant="outlined"
        prominent
        density="compact"
        type="warning"
        class="m-2"
      >
        {{ error }}
      </v-alert>
    </div>
  </div>
  <div v-else>
    <div v-if="iframeSrc" id="my-video-panel">
      <iframe
        width="100%"
        height="500px"
        frameborder="0"
        id="video-iframe"
      ></iframe>
    </div>
    <div :class="wachterLandscape ? 'mx-3' : ''">
      <VideoIframe
        v-if="showVideoIframe"
        :src="videoObject.video.src"
        :authToken="videoObject.authToken"
      />
    </div>
    <VideoJSPlayer
      v-if="showVideoPlayer"
      ref="VideoPlayer"
      :options="videoObject.video"
      :accessToken="videoObject.accessToken"
      @seek="seekVideo"
    />
    <div>
      <div v-if="hasVideoDescription" class="text-center mt-0 border-top">
        <div class="d-flex justify-space-between align-center">
          <!-- Rewind Button -->
          <v-btn
            v-show="videoObject?.seekButtons"
            height="40"
            variant="text"
            @click="seekVideo(-30)"
          >
            <v-icon large color="#4967f3">mdi-rewind-30</v-icon>
          </v-btn>
          <div :class="!videoObject?.seekButtons ? 'mx-auto' : ''">
            {{ videoObject.description }}
          </div>

          <!-- Forward Button -->
          <v-btn
            v-show="videoObject?.seekButtons"
            height="40"
            variant="text"
            @click="seekVideo(30)"
            :disabled="futureDate"
          >
            <v-icon large color="#4967f3">mdi-fast-forward-30</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CameraIntegration } from '../helpers/camera/base';
import VideoJSPlayer from './VideoPlayer.vue';
import VideoIframe from './VideoIframe.vue';
import dayjs from 'dayjs';
import { Auth } from 'aws-amplify';

export default {
  name: 'VideoController',
  components: {
    VideoJSPlayer,
    VideoIframe,
  },
  props: {
    camera: {
      type: CameraIntegration,
      required: true,
    },
    selectedCamera: {
      type: [String, Number],
      default: 0,
    },
  },
  data() {
    return {
      videoObject: null,
      loading: false,
      error: null,
      isMobileLandscape: false,
      dtiqAuthToken: null,
      dtiqIframeUrl: '',
    };
  },
  computed: {
    iframeVideoPlayer() {
      return this.videoObject?.video?.type === 'iframe';
    },
    canvasVideoPlayer() {
      return this.videoObject?.video?.type === 'canvas';
    },
    iframeSrc() {
      // DTIQ 
      if (this.videoObject?.video?.type === 'iframesrc') {
          this.dtiqIframeUrl = this.videoObject.video.src;
          this.getIframeSrc();
      }
      return this.videoObject?.video?.type === 'iframesrc';
    },
    videoJSVideoPlayer() {
      return !this.iframeVideoPlayer && !this.canvasVideoPlayer && !this.iframeSrc;
    },
    showVideoPlayer() {
      return (
        this.videoObject && this.videoObject.video && this.videoJSVideoPlayer
      );
    },
    showVideoIframe() {
      return (
        this.videoObject && this.videoObject.video && this.iframeVideoPlayer
      );
    },
    hasVideoDescription() {
      return this.videoObject?.description;
    },
    wachterLandscape() {
      return (
        this.videoObject?.video?.vendor === 'wachter' && this.isMobileLandscape
      );
    },
    futureDate() {
      const oneMinuteAgo = dayjs().subtract(2, 'minute');
      const date = dayjs(
        this.videoObject?.displayTime,
        'YYYY-MM-DD HH:mm:ss',
      ).isAfter(oneMinuteAgo);
      return date;
    },
  },
  methods: {
    async fetchVideo(seekTime) {
      this.loading = true;
      try {
        this.videoObject = await this.camera.fetchVideo(
          this.selectedCamera,
          seekTime,
        );
      } catch (err) {
        console.error(err);
        if (err.message === 'Missing connection semantics') {
          this.$emit('reload-camera');
        }
        this.error = err.message;
      } finally {
        this.loading = false;
      }
    },
    async seekVideo(seekTime) {
      await this.fetchVideo(seekTime);
      if (this.videoJSVideoPlayer)
        await this.$refs.VideoPlayer.updateVideo(this.videoObject.video);
    },
    load() {
      this.fetchVideo();
    },
    updateMediaQuery() {
      this.isMobileLandscape = window.matchMedia(
        '(orientation: landscape) and (max-width: 900px)',
      ).matches;
    },
    async getIframeSrc() {
      // this function creates the iframe src from our dtiq-iframe api and returns a script that runs dtiq's script
      const currentSession = await Auth.currentSession();
      const { idToken } = currentSession;
      const token = idToken.jwtToken;
      const res = await fetch(this.dtiqIframeUrl, {
        method: 'GET',
        headers: {
          'x-auth-token': token,
        }
      });
      const blob = await res.blob();
      const urlObject = URL.createObjectURL(blob);
      document.querySelector('iframe').setAttribute("src", urlObject)
    },
  },
  mounted() {
    this.load();
    this.updateMediaQuery();
    window.addEventListener('resize', this.updateMediaQuery);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateMediaQuery);
  },
  watch: {
    selectedCamera() {
      this.load();
    },
  },
};
</script>

<style scoped lang="scss">

.video-modal-title {
  color: var(--color-white) !important;
}

.title-loader {
  height: 30px;
  width: 80%;
}

.body-loader {
  height: 300px;
  width: 100%;
}

#video-modal {
  overflow: hidden;
}

.iframe-width {
  width: 750px !important;
}

</style>
